import { useEffect, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { AlertTriangle, PanelLeft, Plus, Sparkles } from 'lucide-react'

import { OutOfCredits } from '@/components/billing/OutOfCredits'
import { ChatTurnView } from '@/components/chat/ChatTurnView'
import { Composer } from '@/components/chat/Composer'
import { ConversationRail } from '@/components/chat/ConversationRail'
import { Button } from '@/components/ui/Controls'
import { EmptyState } from '@/components/ui/States'
import { useAuth } from '@/features/auth/useAuth'
import { useChat } from '@/features/chat/useChat'
import { useChatAvailability } from '@/features/chat/useChatAvailability'
import { useSystemStatus } from '@/features/system/useSystemStatus'
import { env } from '@/config/env'

export function ChatPage() {
  const { user } = useAuth()
  const { turns, conversationId, isStreaming, creditsMessage, send, stop, reset, open } =
    useChat()
  const { data: status } = useSystemStatus()
  const availability = useChatAvailability()
  const bottomRef = useRef<HTMLDivElement>(null)
  // The rail is a column on a wide screen and a drawer on a narrow one, rather than
  // something that disappears below `lg`: on a phone it is the only way back to a thread.
  const [railOpen, setRailOpen] = useState(false)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [turns])

  const unavailable = !env.chatEnabled || availability.data?.enabled === false
  if (unavailable) {
    return (
      <div className="panel">
        <EmptyState
          title="Chat is not available in this deployment."
          hint={
            availability.data?.detail ??
            'Set VITE_ENABLE_CHAT=true and configure a model credential on the server. The observability pages work without it.'
          }
          action={
            <Link
              to="/executions"
              className="text-sm text-primary underline-offset-2 hover:underline"
            >
              Go to executions
            </Link>
          }
        />
      </div>
    )
  }

  const rail = (
    <ConversationRail
      currentId={conversationId}
      disabled={isStreaming}
      onOpen={(id) => {
        setRailOpen(false)
        void open(id)
      }}
      onNew={() => {
        setRailOpen(false)
        void reset()
      }}
    />
  )

  return (
    // Bleeds out of the shell's page padding: a conversation runs to the edges of the
    // window, and the two panes meet on a hairline rather than floating as cards.
    <div className="-mx-4 -my-6 flex h-[calc(100dvh-3.5rem)] sm:-mx-6 lg:-mx-8">
      <div className="hidden w-[21rem] shrink-0 border-r border-line lg:flex lg:flex-col">
        {rail}
      </div>

      {railOpen ? (
        <>
          <button
            type="button"
            aria-label="Close conversations"
            className="fixed inset-0 z-40 bg-black/60 backdrop-blur-[1px] lg:hidden"
            onClick={() => setRailOpen(false)}
          />
          <div className="fixed inset-y-0 left-0 z-50 flex w-80 flex-col border-r border-line
            lg:hidden">
            {rail}
          </div>
        </>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col bg-ground">
        <header className="flex min-h-[3.5rem] shrink-0 flex-wrap items-center justify-between
          gap-x-3 gap-y-2 border-b border-line bg-surface px-4 py-2">
          <div className="min-w-0">
            <h1 className="text-md font-semibold text-ink">Chat</h1>
            <p className="mt-0.5 hidden text-xs text-ink-muted sm:block">
              Each conversation keeps its own memory. Every tool call is shown below and
              recorded in{' '}
              <Link to="/executions" className="text-primary underline-offset-2 hover:underline">
                executions
              </Link>
              .
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              icon={PanelLeft}
              className="lg:hidden !h-7 !w-7 !px-0 sm:!h-8 sm:!w-auto sm:!px-2.5"
              onClick={() => setRailOpen(true)}
              title="Your conversations"
              aria-label="Conversations"
            >
              <span className="hidden sm:inline">Conversations</span>
            </Button>
            {turns.length > 0 ? (
              <Button
                size="sm"
                icon={Plus}
                onClick={() => void reset()}
                disabled={isStreaming}
                className="!h-7 !w-7 !px-0 !border-transparent !bg-transparent !shadow-none sm:!border-line-strong sm:!bg-surface sm:!shadow-card sm:!h-8 sm:!w-auto sm:!px-2.5"
                title="New conversation"
                aria-label="New conversation"
              >
                <span className="hidden sm:inline">New conversation</span>
              </Button>
            ) : null}
          </div>
        </header>

        {creditsMessage ? <OutOfCredits message={creditsMessage} /> : null}

        {user && !user.github_connected ? (
          // A banner rather than a block. The model can still explain what it does and what
          // the tools are for; only the GitHub calls will refuse, and they say why. Locking
          // the whole page for a reason the user can fix in two clicks helps nobody.
          <p className="flex items-start gap-2.5 border-b border-waiting/20 bg-waiting-wash px-4 py-2.5
            text-sm text-ink-dim">
            <AlertTriangle aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-waiting" />
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>GitHub is not connected, so anything touching a repository will refuse.</span>
              <Link
                to="/settings"
                className="font-medium text-primary-bright underline-offset-2 hover:underline"
              >
                Connect GitHub
              </Link>
            </span>
          </p>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {turns.length === 0 ? (
            <EmptyState
              icon={Sparkles}
              title={
                user && !user.github_connected
                  ? 'Ask anything — repository questions need GitHub connected first.'
                  : 'Ask about your repositories.'
              }
              hint={
                status?.read_only_mode
                  ? 'This deployment is read-only, so the assistant can read anything but change nothing.'
                  : 'Reads and writes both run immediately. Every call is recorded in Executions.'
              }
            />
          ) : (
            turns.map((turn) => <ChatTurnView key={turn.turnId} turn={turn} />)
          )}
          <div ref={bottomRef} />
        </div>

        <Composer
          onSend={(value) => void send(value)}
          onStop={() => void stop()}
          isStreaming={isStreaming}
          showSuggestions={turns.length === 0}
        />
      </div>
    </div>
  )
}
